import React, { useState } from 'react';
import { SxStyle } from '../../../types';
import { useStringsContext } from '../../../contexts/strings';
import Grid2 from '@mui/material/Grid2';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove } from '@dnd-kit/sortable';
import Stack from '@mui/material/Stack';
import AppMenuItem from './item/app-menu-item';
import { AppConfigBottomBarItemKey, EditableBottomBarItem, MenuItemEditableKeys } from './types';
import { useFormWithValidation } from '../../../contexts/formWithValidation';
import AppMenuSortableContext from './app-menu-sortable-context';
import AppMenuPreview from './preview/app-menu-preview';
import AppMenuColumn from './app-menu-column';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import DrawerHeader from '../../common/drawer-header';
import { defaultStandardItems } from './items';

const getRandomId = () => Math.floor(Math.random() * (100000 - 10000) + 10000);

const MAX_ITEMS_PRIMARY = 9;

const styles: SxStyle = {
  itemsColumn: {
    minWidth: '350px',
    '@media (max-width: 1100px)': {
      flexGrow: 1,
    },
  },
  itemsContainer: {
    maxWidth: '300px',
  },
  title: {
    borderBottom: '1px solid #e0e0e0',
    paddingTop: 2,
  },
  limit: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: 1,
  },
  card: {
    padding: 2,
    minWidth: '350px',
    maxWidth: '350px',
  },
  numContainer: {
    width: '48px',
    height: '48px',
    backgroundColor: '#003168',
    color: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 1,
  },
  dragOverActive: {
    border: '1px dashed #b4bbc8',
  },
  dragOverInActive: {
    border: 'none',
  },
  previewContainer: {
    minWidth: '350px',
    '@media (max-width: 1100px)': {
      display: 'none',
    },
  },
  previewButton: {
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 2,
    '@media (min-width: 1100px)': {
      display: 'none',
    },
  },
  previewDrawerContent: {
    padding: 2,
  },
};

const homeItem: EditableBottomBarItem = {
  id: getRandomId(),
  type: 'start' as AppConfigBottomBarItemKey,
  text: 'Hem',
  icon: 'Home',
  draggable: false,
  editable: false,
};

const defaultWebItem: EditableBottomBarItem = {
  ...defaultStandardItems['customUrl'],
  id: -1,
  draggable: true,
  editable: true,
};

const AppMenuItems = () => {
  const { strings } = useStringsContext();
  const sensors = useSensors(useSensor(PointerSensor));
  const [menuItemActive, setMenuItemActive] = useState<EditableBottomBarItem | null>(null);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [openPreviewDrawer, setOpenPreviewDrawer] = useState(false);

  const {
    updateFormData: updateBottomBarItems,
    formData: bottomBarItems,
    addFormValidation,
    removeFormValidation,
  } = useFormWithValidation<EditableBottomBarItem[]>();

  const handleDragStart = (event: DragEndEvent) => {
    const { active } = event;
    const menuItem = bottomBarItems.find(({ id }: { id: number }) => id === active.id);
    setMenuItemActive(menuItem!);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = bottomBarItems.findIndex(({ id }: { id: number }) => id === active.id);
      const newIndex = bottomBarItems.findIndex(({ id }: { id: number }) => id === over?.id);
      const updatedMenuItems = arrayMove(bottomBarItems, oldIndex, newIndex);
      updateBottomBarItems(updatedMenuItems);
    }
    setMenuItemActive(null);
  };

  const handleDragCancel = () => {
    setMenuItemActive(null);
  };

  const handleExpanded = (isExpanded: boolean, bottomBarItem: EditableBottomBarItem) => {
    if (isExpanded) {
      setExpandedIds((prev) => [...prev, bottomBarItem.id]);
    } else {
      setExpandedIds((prev) => prev.filter((id) => id !== bottomBarItem.id));
    }
  };

  const handleAdd = () => {
    const newBottomBarItem: EditableBottomBarItem = {
      ...defaultWebItem,
      id: getRandomId(),
    };
    updateBottomBarItems([...bottomBarItems, newBottomBarItem]);
    handleExpanded(true, newBottomBarItem);
  };

  const handleDelete = (menuItem: EditableBottomBarItem) => {
    handleExpanded(false, menuItem);
    const updatedMenuItems = bottomBarItems.filter((item) => item.id !== menuItem.id);
    updateBottomBarItems(updatedMenuItems);
    removeFormValidation(String(menuItem.id));
  };

  const handleOnValue = (
    key: MenuItemEditableKeys,
    value: any | null,
    menuItem: EditableBottomBarItem
  ) => {
    (menuItem as any)[key] = value; // TODO Fix any
    const updatedMenuItems = bottomBarItems.map((item) =>
      item.id === menuItem.id ? menuItem : item
    );
    updateBottomBarItems(updatedMenuItems);
  };

  const handleOnValidation = (key: string, isValid: boolean) => {
    addFormValidation(key, isValid);
  };

  if (!bottomBarItems) {
    return null;
  }

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
      >
        <Grid2 container>
          <Grid2 sx={styles.itemsColumn}>
            <AppMenuColumn title={strings.appMenu.configTitle} divider>
              <Stack direction="column" spacing={2} sx={styles.itemsContainer}>
                <Box sx={styles.previewButton}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={() => setOpenPreviewDrawer(true)}
                    startIcon={<VisibilityIcon />}
                  >
                    {strings.appMenu.previewTitle}
                  </Button>
                </Box>
                <AppMenuSortableContext
                  contextId="primary"
                  title={strings.appMenu.listTitle}
                  subTitle={`${bottomBarItems.length + 1} / ${MAX_ITEMS_PRIMARY + 1}`}
                  bottomBarItems={bottomBarItems}
                  expandedIds={expandedIds}
                  onExpanded={handleExpanded}
                  onDelete={handleDelete}
                  onValue={handleOnValue}
                  onValidation={handleOnValidation}
                  startItem={
                    <AppMenuItem bottomBarItem={homeItem} position={1} key={homeItem.id} />
                  }
                />
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAdd}
                  disabled={bottomBarItems?.length >= MAX_ITEMS_PRIMARY}
                >
                  {strings.appMenu.menuItemAdd}
                </Button>
                <DragOverlay modifiers={[restrictToVerticalAxis]}>
                  {menuItemActive ? (
                    <AppMenuItem bottomBarItem={menuItemActive} isDragging />
                  ) : null}
                </DragOverlay>
              </Stack>
            </AppMenuColumn>
          </Grid2>
          <Grid2 size="grow" sx={styles.previewContainer}>
            <AppMenuColumn title={strings.appMenu.previewTitle}>
              <AppMenuPreview bottomBarItems={bottomBarItems} />
            </AppMenuColumn>
          </Grid2>
        </Grid2>
      </DndContext>
      <Drawer open={openPreviewDrawer} onClose={() => setOpenPreviewDrawer(false)} anchor="right">
        <DrawerHeader
          title={strings.appMenu.previewTitle}
          onClose={() => setOpenPreviewDrawer(false)}
        />
        <Box sx={styles.previewDrawerContent}>
          <AppMenuPreview bottomBarItems={bottomBarItems} />
        </Box>
      </Drawer>
    </>
  );
};

export default AppMenuItems;
